<template>
  <hr-adaptive-form-dialog
    ref="dialog"
    size="sm"
    :title="title"
    no-refocus
    @hide="onDialogHide"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-text-field
        v-model="editDiagnosis.code"
        class="col-12"
        :label="$t('clients.modals.editClient.clinical.codeLabel')"
        :hint="$t('clients.modals.editClient.clinical.codeHint')"
        :rules="codeRules"
        lazy-rules
        autofocus
        @update:model-value="onCodeInput"
      />
      <hr-text-field
        v-model="editDiagnosis.description"
        class="col-12"
        :label="$t('clients.modals.editClient.clinical.descriptionLabel')"
      />
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="$t('common.buttonSave')"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>

<script>
import {
  HrAdaptiveFormDialog, HrConfirmBtn, HrDismissBtn, HrTextField,
} from 'src/design-system';

// ICD-10-CM: a letter, a digit, an alphanumeric, then an optional dot + 1-4 alphanumerics
// (e.g. F84.0, F90.0, U07.1, Z00.129). Format validation only — no code-exists lookup (phase 2).
const ICD10_PATTERN = /^[A-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/;

export default {
  name: 'EditDiagnosisDialog',

  components: {
    HrAdaptiveFormDialog,
    HrConfirmBtn,
    HrDismissBtn,
    HrTextField,
  },

  props: {
    diagnosis: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['ok', 'hide'],

  data() {
    return {
      // Carry the full entry so id/order/dates survive an edit; only code + description are editable here.
      // Shallow spread is correct — the entry is flat and endDate is a Timestamp we must not clone.
      editDiagnosis: {
        code: '',
        description: '',
        ...this.diagnosis,
      },
    };
  },

  computed: {
    isAdding() {
      return !this.diagnosis?.id;
    },

    title() {
      return this.isAdding
        ? this.$t('clients.modals.editClient.clinical.addTitle')
        : this.$t('clients.modals.editClient.clinical.editTitle');
    },

    codeRules() {
      return [
        (val) => !!(val && val.trim())
          || this.$t('clients.modals.editClient.clinical.codeRequiredError'),
        (val) => ICD10_PATTERN.test((val || '').trim().toUpperCase())
          || this.$t('clients.modals.editClient.clinical.codeFormatError'),
      ];
    },
  },

  methods: {
    show() {
      this.$refs.dialog.show();
    },

    hide() {
      this.$refs.dialog?.hide();
    },

    onDialogHide() {
      // Required so the Quasar dialog plugin tears the component down.
      this.$emit('hide');
    },

    onCodeInput(value) {
      // ICD-10 codes are upper-case; normalize case as the user types.
      this.editDiagnosis.code = (value || '').toUpperCase();
    },

    onSubmit() {
      this.editDiagnosis.code = this.editDiagnosis.code.trim();
      this.editDiagnosis.description = (this.editDiagnosis.description || '').trim();
      this.$emit('ok', this.editDiagnosis);
      this.hide();
    },
  },
};
</script>
